import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { CollapsibleCard } from "@/components/shared/CollapsibleCard";
import { formatNOK, formatDate } from "@/lib/format";
import type { DBInvoice, DBProject } from "@/lib/db-types";
import { useManualRevenue } from "@/hooks/use-manual-revenue";
import { usePermissions } from "@/hooks/use-permissions";

interface ManualRevenueSectionProps {
  project: DBProject;
  invoices: DBInvoice[];
}

interface FormState {
  amount: string;
  date: string;
  description: string;
}

const EMPTY_FORM: FormState = { amount: "", date: "", description: "" };

/**
 * Admin-seksjon for manuell inntektsregistrering (VIB-792): rader i
 * `invoices` med is_manual = true som teller i BF/faktureringsstatus.
 * Blokkert på completed-prosjekter (frosset provisjonsgrunnlag + recompute-
 * guard hopper over completed — endres når VIB-1240 lander).
 *
 * Rollegaten speiler RLS-policyene invoices_manual_{insert,update,delete},
 * som krever public.is_admin(). Endres den ene må den andre følge etter.
 */
export function ManualRevenueSection({ project, invoices }: ManualRevenueSectionProps) {
  const { isAdmin } = usePermissions();
  const { addManualRevenue, updateManualRevenue, deleteManualRevenue } = useManualRevenue(project.id);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  if (!isAdmin) return null;

  const isCompleted = project.project_status === "completed";
  const manualRows = invoices
    .filter((inv) => inv.is_manual)
    .sort((a, b) => (b.invoice_date ?? "").localeCompare(a.invoice_date ?? ""));

  const parsedAmount = Number(form.amount.replace(",", "."));
  const formValid = form.amount.trim() !== "" && !Number.isNaN(parsedAmount) && form.date !== "" && form.description.trim() !== "";
  const isSaving = addManualRevenue.isPending || updateManualRevenue.isPending;

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = () => {
    if (!formValid) return;
    if (editingId) {
      const existing = manualRows.find((r) => r.id === editingId);
      updateManualRevenue.mutate(
        {
          invoice_id: editingId,
          project_id: project.id,
          amount_excl_vat: parsedAmount,
          old_amount_excl_vat: existing?.amount_excl_vat ?? null,
          invoice_date: form.date,
          description: form.description.trim(),
        },
        { onSuccess: resetForm },
      );
    } else {
      addManualRevenue.mutate(
        {
          project_id: project.id,
          amount_excl_vat: parsedAmount,
          invoice_date: form.date,
          description: form.description.trim(),
        },
        { onSuccess: resetForm },
      );
    }
  };

  const startEdit = (row: DBInvoice) => {
    setEditingId(row.id);
    setForm({
      amount: String(row.amount_excl_vat ?? ""),
      date: row.invoice_date ?? "",
      description: row.description ?? "",
    });
    setShowForm(true);
  };

  return (
    <CollapsibleCard
      title="Manuell inntekt"
      icon={<SrIcon name="pencil" className="w-5 h-5 text-primary" />}
      count={manualRows.length > 0 ? manualRows.length : undefined}
      actions={(open) =>
        open ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => {
              setEditingId(null);
              setForm(EMPTY_FORM);
              setShowForm(true);
            }}
            disabled={showForm || isCompleted}
            title={
              isCompleted
                ? "Prosjektet er fullført — provisjonsgrunnlaget er frosset (VIB-1240)"
                : undefined
            }
          >
            <SrIcon name="plus" className="w-3 h-3 mr-1" /> Registrer inntekt
          </Button>
        ) : null
      }
      contentClassName="border-t"
    >
      <div className="p-4 space-y-3">
        <p className="text-xs text-muted-foreground">
          Inntekt uten PowerOffice-faktura (f.eks. manuelle bilag ført direkte i Go). Teller i
          GEEK reell og faktureringsstatus. Kun synlig for ledere.
          {isCompleted && " Prosjektet er fullført — nye rader er blokkert inntil netting av sene endringer (VIB-1240) er på plass."}
        </p>

        {showForm && (
          <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Beløp eks. mva
              <Input
                type="text"
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="F.eks. 50000 eller -10000"
                className="h-8 w-40"
                aria-label="Beløp eks. mva"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Dato
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="h-8 w-36"
                aria-label="Dato for manuell inntekt"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground flex-1 min-w-[200px]">
              Beskrivelse / referanse
              <Input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="F.eks. Manuelt bilag PO #1234"
                className="h-8"
                aria-label="Beskrivelse for manuell inntekt"
              />
            </label>
            <div className="flex gap-2">
              <Button size="sm" className="h-8" onClick={handleSubmit} disabled={!formValid || isSaving}>
                {isSaving ? "Lagrer…" : editingId ? "Oppdater" : "Lagre"}
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={resetForm} disabled={isSaving}>
                Avbryt
              </Button>
            </div>
          </div>
        )}

        {manualRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen manuelle inntektsrader.</p>
        ) : (
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Dato</th>
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Beskrivelse</th>
                <th className="text-right py-2 pr-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Eks. mva</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {manualRows.map((row) => (
                <tr key={row.id} className="border-b border-border/50 last:border-0">
                  <td className="py-2 pr-4 text-muted-foreground">{formatDate(row.invoice_date)}</td>
                  <td className="py-2 pr-4">{row.description ?? "—"}</td>
                  <td className="py-2 pr-4 text-right tabular-nums font-medium">
                    {formatNOK(row.amount_excl_vat ?? 0)}
                  </td>
                  <td className="py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(row)}
                        disabled={isCompleted}
                        className="text-muted-foreground hover:text-primary disabled:opacity-40"
                        title="Rediger manuell inntekt"
                      >
                        <SrIcon name="pencil" className="w-4 h-4" />
                      </button>
                      {confirmDeleteId === row.id ? (
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-6 px-2 text-[11px]"
                          disabled={deleteManualRevenue.isPending}
                          onClick={() =>
                            deleteManualRevenue.mutate(row, {
                              onSettled: () => setConfirmDeleteId(null),
                            })
                          }
                        >
                          {deleteManualRevenue.isPending ? "Sletter…" : "Bekreft sletting"}
                        </Button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(row.id)}
                          disabled={isCompleted}
                          className="text-muted-foreground hover:text-destructive disabled:opacity-40"
                          title="Slett manuell inntekt"
                        >
                          <SrIcon name="trash-2" className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </CollapsibleCard>
  );
}
